import { useOrganizationList } from "@/api/services/admin/organization";
import { IconSearch } from "@/assets/icons";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { SegmentedControl } from "@/components/elements/SegmentedControl/SegmentedControl";
import { SimplePagination } from "@/components/elements/SimplePagination";
import { Table } from "@/components/elements/Table";
import { OrganizationSelectFilterPopover } from "@/features/session/components/OrganizationSelectFilterPopover";
import { ORG_SELECT_FIXED_COLUMN_ID } from "@/features/session/constants/organizationSelectColumns";
import { ORGANIZATION_TABS, ORGANIZATION_TAB_STATUS, TOrganizationTabValue } from "@/features/session/constants/organizationTabs";
import { useOrganizationSelectColumns } from "@/features/session/hooks/useOrganizationSelectColumns";
import { useOrganizationSelectFilterCache } from "@/features/session/providers/OrganizationSelectFilterCacheProvider";
import { useDebounce } from "@/hooks/useDebounce";
import { usePagination } from "@/hooks/usePagination";
import { useTable } from "@/hooks/useTable";
import { iconColor } from "@/theme/colors";
import { TApiSortsReq } from "@/types/api";
import { InputAdornment, Stack, SxProps, Theme, styled } from "@mui/material";
import { FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";

type TOrganizationSelectCardProps = {
  selectedOrgId: string;
  onSelectOrg: (id: string) => void;
  sx?: SxProps<Theme>;
};

export const OrganizationSelectCard: FC<TOrganizationSelectCardProps> = ({ selectedOrgId, onSelectOrg, sx }) => {
  const { filterCache, setFilterCache } = useOrganizationSelectFilterCache();
  const pagReq = usePagination();

  const [activeTab, setActiveTab] = useState<TOrganizationTabValue>("active");

  // When a popover filter is active, the free-text search is disabled and ignored
  // (mirrors the main organization list search behavior).
  const hasFilter = !!(
    filterCache.externalId ||
    filterCache.name ||
    filterCache.productId ||
    filterCache.lastLoginFrom ||
    filterCache.lastLoginTo ||
    filterCache.createdDateFrom ||
    filterCache.createdDateTo
  );

  const [searchInput, setSearchInput] = useState(filterCache.search);
  const appliedSearch = useRef(filterCache.search);

  // Sync local input when the search condition changes externally (e.g. tab/filter reset).
  useEffect(() => {
    setSearchInput(filterCache.search);
  }, [filterCache.search]);

  const applySearch = (rawValue: string) => {
    const value = rawValue.trim();
    if (appliedSearch.current === value) return;
    appliedSearch.current = value;
    setFilterCache({ ...filterCache, search: value, page: 1 });
  };

  // Auto-search (debounced ~1s) fires only for empty input (clear) or 2+ chars, so a
  // single stray char — or a mid-IME katakana composition — never triggers a query.
  useDebounce(() => {
    const value = (searchInput ?? "").trim();
    if (value.length === 1) return;
    applySearch(value);
  }, [searchInput]);

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") applySearch((e.target as HTMLInputElement).value);
  };

  const { data, isLoading, isFetching } = useOrganizationList({
    page: filterCache.page,
    search: hasFilter ? undefined : filterCache.search || undefined,
    status: ORGANIZATION_TAB_STATUS[activeTab],
    excludeDeleted: true,
    externalId: filterCache.externalId || undefined,
    name: filterCache.name || undefined,
    productId: filterCache.productId || undefined,
    lastLoginFrom: filterCache.lastLoginFrom ?? undefined,
    lastLoginTo: filterCache.lastLoginTo ?? undefined,
    createdDateFrom: filterCache.createdDateFrom ?? undefined,
    createdDateTo: filterCache.createdDateTo ?? undefined,
    sorts: [pagReq?.sorts ?? [], "impersonationCreatedAt|DESC"].flat() as TApiSortsReq[],
  });

  const organizations = data?.data ?? [];
  const totalPages = data?.pagination?.totalPage ?? 1;

  const handleChangeTab = (tab: TOrganizationTabValue) => {
    setActiveTab(tab);
    setFilterCache({ ...filterCache, page: 1 });
    onSelectOrg("");
  };

  const handleChangePage = (page: number) => {
    setFilterCache({ ...filterCache, page });
  };

  const { columns } = useOrganizationSelectColumns({ selectedOrgId, onSelectOrg });

  const columnOrder = useMemo(() => [ORG_SELECT_FIXED_COLUMN_ID, ...filterCache.columnOrder], [filterCache.columnOrder]);

  const { table } = useTable({
    columns,
    rows: organizations,
    onClickRow: (row) => onSelectOrg(String(row.id)),
    state: { columnVisibility: filterCache.visibleColumns, columnOrder },
  });

  return (
    <InnerBox sx={sx} padding="24px">
      <Stack flexDirection="column" gap={2}>
        <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between">
          <Stack flexDirection="row" alignItems="center" gap={2}>
            <Search
              nonControl
              placeholder={hasFilter ? "フィルタ適用済み" : "組織ID／組織名で検索"}
              value={hasFilter ? "" : searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onBlur={(e) => applySearch(e.target.value)}
              disabled={hasFilter}
              clearable={!hasFilter}
              sx={{ flex: 1, padding: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end">
                    <IconSearch height={24} width={24} color={iconColor.readOnly} />
                  </InputAdornment>
                ),
              }}
            />
            <OrganizationSelectFilterPopover />
          </Stack>
          <SegmentedControl value={activeTab} options={ORGANIZATION_TABS} onChange={handleChangeTab} />
        </Stack>

        <Stack flexDirection="column" gap={3}>
          <Table table={table} isLoading={isLoading} isFetching={isFetching} maxHeight="45vh" />
          <SimplePagination page={filterCache.page} totalPage={totalPages} onChange={handleChangePage} />
        </Stack>
      </Stack>
    </InnerBox>
  );
};

const Search = styled(Input)`
  .MuiOutlinedInput-root {
    padding: 0;
    max-width: 240px;
  }
`;
